// Centralized route configuration for multilingual URL structure
// English routes have no prefix (default), other languages have /{lang}/ prefix

import { Language } from './translations-meta';

// Route keys used throughout the application
export type RouteKey =
  | 'home'
  | 'pricing'
  | 'features'
  | 'contact'
  | 'about'
  | 'blog'
  | 'faq'
  | 'testimonials'
  | 'integrations'
  | 'productPms'
  | 'productChannelManager'
  | 'productWebsiteBuilder'
  | 'forWhomSmallBusiness'
  | 'forWhomMediumLarge'
  | 'briefDescription'
  | 'terms'
  | 'privacy'
  | 'publicationTerms'
  | 'websiteTerms'
  | 'compare'
  | 'compareBestVacationRentalSoftware'
  | 'ai'
  | 'vacationRentalPms';

// Localized route mappings per language
// English has no prefix, other languages have /{lang}/ prefix with translated slugs
export const localizedRoutes: Record<Language, Record<RouteKey, string>> = {
  en: {
    home: '/',
    pricing: '/pricing',
    features: '/features',
    contact: '/contact',
    about: '/about',
    blog: '/blog',
    faq: '/faq',
    testimonials: '/testimonials',
    integrations: '/integrations',
    productPms: '/product/holiday-rental-software',
    productChannelManager: '/product/channel-manager-holiday-rentals',
    productWebsiteBuilder: '/product/website-builder',
    forWhomSmallBusiness: '/solutions/small-business',
    forWhomMediumLarge: '/solutions/medium-large',
    briefDescription: '/what-is-irent',
    terms: '/terms',
    privacy: '/privacy',
    publicationTerms: '/publication-terms',
    websiteTerms: '/website-development-terms',
    compare: '/compare',
    compareBestVacationRentalSoftware: '/compare/best-holiday-rental-software',
    ai: '/ai',
    vacationRentalPms: '/holiday-rental-pms',
  },
  nl: {
    home: '/nl',
    pricing: '/nl/prijzen',
    features: '/nl/functies',
    contact: '/nl/contact',
    about: '/nl/over-ons',
    blog: '/nl/blog',
    faq: '/nl/faq',
    testimonials: '/nl/referenties',
    integrations: '/nl/integraties',
    productPms: '/nl/product/vakantieverhuur-software',
    productChannelManager: '/nl/product/channel-manager-vakantieverhuur',
    productWebsiteBuilder: '/nl/product/website-bouwer',
    forWhomSmallBusiness: '/nl/oplossingen/kleine-verhuurders',
    forWhomMediumLarge: '/nl/oplossingen/grotere-verhuurders',
    briefDescription: '/nl/wat-is-irent',
    terms: '/nl/voorwaarden',
    privacy: '/nl/privacy',
    publicationTerms: '/nl/publicatievoorwaarden',
    websiteTerms: '/nl/website-voorwaarden',
    compare: '/nl/vergelijken',
    compareBestVacationRentalSoftware: '/nl/vergelijken/beste-vakantieverhuur-software',
    ai: '/nl/ai',
    vacationRentalPms: '/nl/vakantieverhuur-pms',
  },
  es: {
    home: '/es',
    pricing: '/es/precios',
    features: '/es/funcionalidades',
    contact: '/es/contacto',
    about: '/es/nosotros',
    blog: '/es/blog',
    faq: '/es/faq',
    testimonials: '/es/testimonios',
    integrations: '/es/integraciones',
    productPms: '/es/producto/software-alquiler-vacacional',
    productChannelManager: '/es/producto/channel-manager-alquiler-vacacional',
    productWebsiteBuilder: '/es/producto/creador-web',
    forWhomSmallBusiness: '/es/soluciones/pequenos-propietarios',
    forWhomMediumLarge: '/es/soluciones/grandes-propietarios',
    briefDescription: '/es/que-es-irent',
    terms: '/es/terminos',
    privacy: '/es/privacidad',
    publicationTerms: '/es/terminos-publicacion',
    websiteTerms: '/es/terminos-desarrollo-web',
    compare: '/es/comparar',
    compareBestVacationRentalSoftware: '/es/comparar/mejor-software-alquiler-vacacional',
    ai: '/es/ia',
    vacationRentalPms: '/es/pms-alquiler-vacacional',
  },
  de: {
    home: '/de',
    pricing: '/de/preise',
    features: '/de/funktionen',
    contact: '/de/kontakt',
    about: '/de/ueber-uns',
    blog: '/de/blog',
    faq: '/de/faq',
    testimonials: '/de/referenzen',
    integrations: '/de/integrationen',
    productPms: '/de/produkt/ferienvermietung-software',
    productChannelManager: '/de/produkt/channel-manager-ferienvermietung',
    productWebsiteBuilder: '/de/produkt/website-baukasten',
    forWhomSmallBusiness: '/de/loesungen/kleine-vermieter',
    forWhomMediumLarge: '/de/loesungen/grosse-vermieter',
    briefDescription: '/de/was-ist-irent',
    terms: '/de/agb',
    privacy: '/de/datenschutz',
    publicationTerms: '/de/veroeffentlichungsbedingungen',
    websiteTerms: '/de/website-entwicklungsbedingungen',
    compare: '/de/vergleichen',
    compareBestVacationRentalSoftware: '/de/vergleichen/beste-ferienvermietung-software',
    ai: '/de/ki',
    vacationRentalPms: '/de/ferienvermietung-pms',
  },
  fr: {
    home: '/fr',
    pricing: '/fr/tarifs',
    features: '/fr/fonctionnalites',
    contact: '/fr/contact',
    about: '/fr/a-propos',
    blog: '/fr/blog',
    faq: '/fr/faq',
    testimonials: '/fr/temoignages',
    integrations: '/fr/integrations',
    productPms: '/fr/produit/logiciel-location-vacances',
    productChannelManager: '/fr/produit/channel-manager-location-vacances',
    productWebsiteBuilder: '/fr/produit/createur-site',
    forWhomSmallBusiness: '/fr/solutions/petits-proprietaires',
    forWhomMediumLarge: '/fr/solutions/grands-proprietaires',
    briefDescription: '/fr/quest-ce-que-irent',
    terms: '/fr/conditions',
    privacy: '/fr/confidentialite',
    publicationTerms: '/fr/conditions-publication',
    websiteTerms: '/fr/conditions-developpement-web',
    compare: '/fr/comparer',
    compareBestVacationRentalSoftware: '/fr/comparer/meilleur-logiciel-location-vacances',
    ai: '/fr/ia',
    vacationRentalPms: '/fr/pms-location-vacances',
  },
  it: {
    home: '/it',
    pricing: '/it/prezzi',
    features: '/it/funzionalita',
    contact: '/it/contatti',
    about: '/it/chi-siamo',
    blog: '/it/blog',
    faq: '/it/faq',
    testimonials: '/it/testimonianze',
    integrations: '/it/integrazioni',
    productPms: '/it/prodotto/software-affitti-brevi',
    productChannelManager: '/it/prodotto/channel-manager-affitti-brevi',
    productWebsiteBuilder: '/it/prodotto/costruttore-siti',
    forWhomSmallBusiness: '/it/soluzioni/piccoli-proprietari',
    forWhomMediumLarge: '/it/soluzioni/grandi-proprietari',
    briefDescription: '/it/cose-irent',
    terms: '/it/termini',
    privacy: '/it/privacy',
    publicationTerms: '/it/termini-pubblicazione',
    websiteTerms: '/it/termini-sviluppo-web',
    compare: '/it/confronto',
    compareBestVacationRentalSoftware: '/it/confronto/miglior-software-affitti-vacanze',
    ai: '/it/ia',
    vacationRentalPms: '/it/pms-affitti-brevi',
  },
  pt: {
    home: '/pt',
    pricing: '/pt/precos',
    features: '/pt/funcionalidades',
    contact: '/pt/contacto',
    about: '/pt/sobre-nos',
    blog: '/pt/blog',
    faq: '/pt/faq',
    testimonials: '/pt/testemunhos',
    integrations: '/pt/integracoes',
    productPms: '/pt/produto/software-alojamento-local',
    productChannelManager: '/pt/produto/channel-manager-alojamento-local',
    productWebsiteBuilder: '/pt/produto/criador-sites',
    forWhomSmallBusiness: '/pt/solucoes/pequenos-proprietarios',
    forWhomMediumLarge: '/pt/solucoes/grandes-proprietarios',
    briefDescription: '/pt/o-que-e-irent',
    terms: '/pt/termos',
    privacy: '/pt/privacidade',
    publicationTerms: '/pt/termos-publicacao',
    websiteTerms: '/pt/termos-desenvolvimento-web',
    compare: '/pt/comparar',
    compareBestVacationRentalSoftware: '/pt/comparar/melhor-software-alojamento-local',
    ai: '/pt/ia',
    vacationRentalPms: '/pt/pms-alojamento-local',
  },
  dk: {
    home: '/dk',
    pricing: '/dk/priser',
    features: '/dk/funktioner',
    contact: '/dk/kontakt',
    about: '/dk/om-os',
    blog: '/dk/blog',
    faq: '/dk/faq',
    testimonials: '/dk/anmeldelser',
    integrations: '/dk/integrationer',
    productPms: '/dk/produkt/feriebolig-software',
    productChannelManager: '/dk/produkt/channel-manager-feriebolig',
    productWebsiteBuilder: '/dk/produkt/hjemmeside-bygger',
    forWhomSmallBusiness: '/dk/loesninger/smaa-udlejere',
    forWhomMediumLarge: '/dk/loesninger/store-udlejere',
    briefDescription: '/dk/hvad-er-irent',
    terms: '/dk/vilkaar',
    privacy: '/dk/privatlivspolitik',
    publicationTerms: '/dk/publikationsvilkaar',
    websiteTerms: '/dk/webstedsudviklingsvilkaar',
    compare: '/dk/sammenlign',
    compareBestVacationRentalSoftware: '/dk/sammenlign/bedste-feriebolig-software',
    ai: '/dk/ai',
    vacationRentalPms: '/dk/feriebolig-pms',
  },
};

export function stripHtmlSuffix(pathname: string): string {
  return pathname.endsWith('.html') ? pathname.slice(0, -5) : pathname;
}

export function getPrerenderPath(pathname: string): string {
  // Always return clean SPA routes. Sidecar .html files still exist in dist/
  // for direct hits, but internal links, canonicals, hreflangs and the sitemap
  // must reference the clean URL to avoid duplicate-content signals.
  return stripHtmlSuffix(pathname);
}


// Reverse lookup: find route key from pathname
export function getRouteKeyFromPath(pathname: string): RouteKey | null {
  const normalizedPath = stripHtmlSuffix(pathname).replace(/\/$/, '') || '/';
  // Check all languages for matching path
  for (const lang of Object.keys(localizedRoutes) as Language[]) {
    const routes = localizedRoutes[lang];
    for (const [key, path] of Object.entries(routes)) {
      if (path === normalizedPath) {
        return key as RouteKey;
      }
    }
  }
  return null;
}

// Get the localized path for a route key and language
export function getLocalizedPath(routeKey: RouteKey, language: Language): string {
  return getPrerenderPath(localizedRoutes[language]?.[routeKey] || localizedRoutes.en[routeKey]);
}

// True when a route key resolves to a non-empty path for the given language
// (falling back to EN). Callers use this to gate UI that links to pages
// that may not be shipped in every language yet.
export function isRouteAvailable(
  routeKey: RouteKey | undefined | null,
  language: Language,
): boolean {
  if (!routeKey) return false;
  const path =
    localizedRoutes[language]?.[routeKey] || localizedRoutes.en?.[routeKey] || '';
  return typeof path === 'string' && path.trim().length > 0;
}

// Detect language from URL path
export function detectLanguageFromPath(pathname: string): Language | null {
  const normalizedPath = stripHtmlSuffix(pathname);
  const langMatch = normalizedPath.match(/^\/([a-z]{2})(\/|$)/);
  if (langMatch) {
    const lang = langMatch[1] as Language;
    if (lang in localizedRoutes) {
      return lang;
    }
  }
  return null; // Default - no language prefix means English
}

// Get the equivalent path in another language
export function getEquivalentPath(currentPath: string, targetLanguage: Language): string {
  const normalizedPath = stripHtmlSuffix(currentPath);

  // Admin routes are language-agnostic — strip any lang prefix and keep as-is
  const adminMatch = normalizedPath.match(/^(?:\/[a-z]{2})?(\/admin.*)/);
  if (adminMatch) {
    return adminMatch[1];
  }

  // App store privacy policy pages live on a single URL — keep the user there
  // when switching language; the page has its own in-page language switcher.
  if (/^\/app-(owners|maintenance|property-management)-privacy-policy$/.test(normalizedPath)) {
    return normalizedPath;
  }

  // Find the current route key
  const routeKey = getRouteKeyFromPath(normalizedPath);
  
  if (routeKey) {
    return getLocalizedPath(routeKey, targetLanguage);
  }
  
  // Handle blog post URLs - they keep the same slug structure
  const blogMatch = normalizedPath.match(/^(?:\/[a-z]{2})?\/blog\/(.+)$/);
  if (blogMatch) {
    const slug = blogMatch[1];
    return getPrerenderPath(targetLanguage === 'en' ? `/blog/${slug}` : `/${targetLanguage}/blog/${slug}`);
  }
  
  // Fallback: if we can't find a mapping, go to home
  return getPrerenderPath(targetLanguage === 'en' ? '/' : `/${targetLanguage}`);
}

// Get all hreflang URLs for a given route key
export function getHreflangUrls(routeKey: RouteKey, baseUrl: string): Record<string, string> {
  const urls: Record<string, string> = {};
  
  for (const lang of Object.keys(localizedRoutes) as Language[]) {
    // Convert 'dk' to 'da' for hreflang
    const hreflangCode = lang === 'dk' ? 'da' : lang;
    urls[hreflangCode] = `${baseUrl}${getPrerenderPath(localizedRoutes[lang][routeKey])}`;
  }
  
  // Add x-default pointing to English
  urls['x-default'] = `${baseUrl}${getPrerenderPath(localizedRoutes.en[routeKey])}`;
  
  return urls;
}

/**
 * Get all hreflang URLs for a blog post.
 *
 * Blog slugs are translated per language (blog_translations.slug), so a single
 * slug must NOT be reused for all 8 alternates — that would advertise URLs that
 * only 301 to the real translated URL and break hreflang reciprocity.
 * Pass `slugsByLanguage` (language code -> translated slug) whenever it is
 * known; missing languages fall back to the given slug.
 */
export function getBlogPostHreflangUrls(
  slug: string,
  baseUrl: string,
  slugsByLanguage?: Partial<Record<Language, string>>,
): Record<string, string> {
  const urls: Record<string, string> = {};
  const slugFor = (lang: Language) => slugsByLanguage?.[lang] || slug;

  for (const lang of Object.keys(localizedRoutes) as Language[]) {
    const hreflangCode = lang === 'dk' ? 'da' : lang;
    const s = slugFor(lang);
    const path = getPrerenderPath(lang === 'en' ? `/blog/${s}` : `/${lang}/blog/${s}`);
    urls[hreflangCode] = `${baseUrl}${path}`;
  }

  urls['x-default'] = `${baseUrl}${getPrerenderPath(`/blog/${slugFor('en')}`)}`;

  return urls;
}

